import type { ComponentType } from 'react'
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom'

import { PaginaErro } from '@/shared/ui/PaginaErro.jsx'
import LayoutPublico from '@/storefront/layout/LayoutPublico.jsx'
import PaginaInicial from '@/storefront/paginas/PaginaInicial.jsx'

/** Transforma um import dinâmico no formato que o `lazy` do RR7 espera. */
const pagina =
  (importar: () => Promise<{ default: ComponentType }>) =>
  async () => ({ Component: (await importar()).default })

/* ─────────────────────────────── painel ───────────────────────────────── */
/*
 * Duas entradas de topo separadas, de propósito:
 *  - /admin/login fica FORA do AdminRoot. Dentro dele, a RotaProtegida
 *    redirecionaria o login para o login, em laço;
 *  - /admin/* herda a guarda, então rota nova nasce protegida.
 *
 * Todo o painel é lazy: quem só visita a vitrine não baixa nada disto, nem o
 * react-hook-form que os formulários usam.
 */
const rotasAdmin: RouteObject[] = [
  {
    path: '/admin/login',
    lazy: pagina(() => import('@/admin/paginas/PaginaLogin.jsx')),
  },
  {
    path: '/admin',
    lazy: pagina(() => import('@/admin/AdminRoot.jsx')),
    children: [
      { index: true, lazy: pagina(() => import('@/admin/paginas/PaginaDashboard.jsx')) },
      { path: 'produtos', lazy: pagina(() => import('@/admin/paginas/PaginaListaProdutos.jsx')) },
      // 'nova' ANTES de ':id': o Fastify prioriza segmento estático, mas o
      // React Router casa na ordem declarada — invertido, /produtos/nova
      // cairia no formulário de edição com id='nova'.
      { path: 'produtos/nova', lazy: pagina(() => import('@/admin/paginas/PaginaProdutoForm.jsx')) },
      { path: 'produtos/:id', lazy: pagina(() => import('@/admin/paginas/PaginaProdutoForm.jsx')) },
      { path: 'configuracoes', lazy: pagina(() => import('@/admin/paginas/PaginaConfiguracoes.jsx')) },
      { path: 'trocar-senha', lazy: pagina(() => import('@/admin/paginas/PaginaTrocarSenha.jsx')) },
      // Caminho desconhecido dentro do painel volta para o início dele, não
      // para o 404 da vitrine.
      { path: '*', element: <Navigate to="/admin" replace /> },
    ],
  },
]

const rotasPublicas: RouteObject[] = [
  // A home é EAGER: ela é o primeiro paint, e adiar num chunk só atrasaria o LCP.
  { index: true, element: <PaginaInicial /> },

  {
    path: 'bicicletas',
    lazy: pagina(() => import('@/storefront/paginas/PaginaCatalogo.jsx')),
  },
  {
    path: 'bicicletas/:slug',
    lazy: pagina(() => import('@/storefront/paginas/PaginaProduto.jsx')),
  },

  // Atalhos: mantêm vivo qualquer link antigo já compartilhado no WhatsApp.
  { path: 'bikes', element: <Navigate to="/bicicletas" replace /> },
  { path: 'novas', element: <Navigate to="/bicicletas?condicao=NOVA" replace /> },
  { path: 'usadas', element: <Navigate to="/bicicletas?condicao=USADA" replace /> },

  { path: '*', lazy: pagina(() => import('@/storefront/paginas/PaginaNaoEncontrada.jsx')) },
]

/**
 * `errorElement` em CADA entrada de topo, não só numa.
 *
 * Sem ele, uma exceção no render derruba a árvore inteira do React e o
 * visitante fica com página EM BRANCO — numa loja, isso é venda perdida em
 * silêncio. Ele também pega a falha de carregar chunk que acontece quando o
 * `index.html` em cache do visitante aponta para arquivos de um deploy
 * anterior.
 *
 * Fica em todas as entradas porque o React Router sobe o erro até o ancestral
 * mais próximo QUE TENHA um: declarar só no layout público deixaria o painel
 * sem rede de proteção.
 */
export const router = createBrowserRouter([
  // O painel vem ANTES do layout público: sem isso, /admin/* cairia no splat
  // '*' da vitrine e renderizaria o 404 com navbar e rodapé da loja.
  ...rotasAdmin.map((rota) => ({ ...rota, errorElement: <PaginaErro /> })),
  {
    element: <LayoutPublico />,
    errorElement: <PaginaErro />,
    children: rotasPublicas,
  },
])
